"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.Geometry3D = exports.Geometry3DCreationMethod = void 0;
const tslib_1 = require("tslib");
const Matrix = tslib_1.__importStar(require("../matrix"));
const vector_1 = require("./../vector");
const CameraCalibration = tslib_1.__importStar(require("./camera.calibration"));
const Project = tslib_1.__importStar(require("./coordinate.projection"));
const GeoUtil = tslib_1.__importStar(require("./geometry.util"));
var Geometry3DCreationMethod;
(function (Geometry3DCreationMethod) {
    Geometry3DCreationMethod[Geometry3DCreationMethod["ALIGNMENT_GRID"] = 0] = "ALIGNMENT_GRID";
    Geometry3DCreationMethod[Geometry3DCreationMethod["PITCH"] = 1] = "PITCH";
})(Geometry3DCreationMethod || (exports.Geometry3DCreationMethod = Geometry3DCreationMethod = {}));
/**
 * All variables in this file will have different suffixes based on what coordinate system they are in
 *
 * SC  - Screen Coordinates (2d)
 * CI  - Calibrated Image (2d)
 * IP  - Image Plane (2d) - Measured in Pixels with 0,0 at the center of the Raw Bitmap
 * MRW - Measurement Real World (3d)
 * MP  - Measurement Plane (2d)
 *
 */
class Geometry3D {
    constructor(ctx) {
        this.planeCenter_MRW = ctx.planeCenter_MRW;
        this.planeNormal_MRW = ctx.planeNormal_MRW;
        this.planeAxis_MP = ctx.planeAxis_MP;
        this.planeOrigin_MRW = ctx.planeOrigin_MRW;
        this.distance = ctx.ctx.distance;
        this.resolution = ctx.ctx.resolution;
        this.focalLength = ctx.focalLength;
        this.calibration = ctx.calibration;
        if (this.calibration) {
            var rotation = this.calibration.rotation;
            this.rotation = Matrix.createRotationMatrix(rotation.x, rotation.y, rotation.z);
        }
        this.homography = ctx.homography;
    }
    getHomography() {
        return this.homography;
    }
    getDistance() {
        return this.distance;
    }
    getFocalLength() {
        return this.focalLength;
    }
    getPlaneNormal() {
        return this.planeNormal_MRW;
    }
    getPlaneCenter() {
        return this.planeCenter_MRW;
    }
    getPlaneOrigin() {
        return this.planeOrigin_MRW;
    }
    getPlaneAxis() {
        return this.planeAxis_MP;
    }
    /**
     * Perspective transform the points of a quad based on a single point being moved.
     *
     * @param points Points of quad to wonk.
     * @param index Index of point that was moved.
     * @returns {Vector[]} List of location
     */
    wonkQuad(points, index) {
        var quad = [];
        var POINTS = GeoUtil.POINTS;
        if (index % 2 === 0) {
            quad[POINTS.TOP_LEFT] = this.perspectiveTransform(this.projectCalibratedToImage(points[POINTS.TOP_LEFT]));
            quad[POINTS.BOTTOM_RIGHT] = this.perspectiveTransform(this.projectCalibratedToImage(points[POINTS.BOTTOM_RIGHT]));
            quad[POINTS.TOP_RIGHT] = new vector_1.Vector(quad[POINTS.BOTTOM_RIGHT].x, quad[POINTS.TOP_LEFT].y);
            quad[POINTS.BOTTOM_LEFT] = new vector_1.Vector(quad[POINTS.TOP_LEFT].x, quad[POINTS.BOTTOM_RIGHT].y);
        }
        else {
            quad[POINTS.TOP_RIGHT] = this.perspectiveTransform(this.projectCalibratedToImage(points[POINTS.TOP_RIGHT]));
            quad[POINTS.BOTTOM_LEFT] = this.perspectiveTransform(this.projectCalibratedToImage(points[POINTS.BOTTOM_LEFT]));
            quad[POINTS.TOP_LEFT] = new vector_1.Vector(quad[POINTS.BOTTOM_LEFT].x, quad[POINTS.TOP_RIGHT].y);
            quad[POINTS.BOTTOM_RIGHT] = new vector_1.Vector(quad[POINTS.TOP_RIGHT].x, quad[POINTS.BOTTOM_LEFT].y);
        }
        return quad.map(this.projectMeasureToCalibrated, this);
    }
    /**
     * Move a point on the image by a distance measured in the real world.
     *
     * eg move pixel (0, 20) by (2m, 5m)
     *
     * @param start_CI Start point
     * @param moveAmount_MRW Movement amount (Measured in meters)
     * @returns {Vector} End point on CI.
     */
    movePoint(start_CI, moveAmount_MRW) {
        var start_MRW = this.projectCalibratedToWorld(start_CI);
        var moveX = this.planeAxis_MP.x.multiply(moveAmount_MRW.x);
        var moveY = this.planeAxis_MP.y.multiply(moveAmount_MRW.y);
        var end_MRW = start_MRW.add(moveX).add(moveY);
        return this.projectWorldToCalibrated(end_MRW);
    }
    /**
     * Calculate the distance between two points on the Calibrated image.
     *
     * @param start_CI Start point
     * @param end_CI End point
     * @returns {number} Distance in meters
     */
    calculateDistance(start_CI, end_CI) {
        let start_MRW = this.projectCalibratedToWorld(start_CI);
        let end_MRW = this.projectCalibratedToWorld(end_CI);
        return start_MRW.subtract(end_MRW).length();
    }
    /**
 * subtract two image points and return the vector in world projection
 *
 * @param start_CI Start vector in image projection
 * @param end_CI End vector in image projection
 * @returns {vector} Result vector in world projection
 */
    calculateWorldDifferenceVector(start_CI, end_CI) {
        let start_MRW = this.projectCalibratedToWorld(start_CI);
        let end_MRW = this.projectCalibratedToWorld(end_CI);
        return start_MRW.subtract(end_MRW);
    }
    /**
     * Calculate the area of a collection of points on the Calibrated image.
     *
     * @param points_CI Start point
     * @returns {number} Distance in meters
     */
    calculateArea(points_CI) {
        var area = 0;
        var points_MP = points_CI.map(this.projectCalibratedToMeasure, this);
        if (points_MP.length < 3) {
            return NaN;
        }
        var backPoint_MP = points_MP[points_MP.length - 1];
        for (var i = 0; i < points_MP.length; i++) {
            area += (backPoint_MP.x - points_MP[i].x) * (backPoint_MP.y + points_MP[i].y);
            backPoint_MP = points_MP[i];
        }
        return Math.abs(area / 2);
    }
    perspectiveTransform(p) {
        var m = this.homography;
        var x = p.x;
        var y = p.y;
        var w = x * m[6] + y * m[7] + m[8];
        if (Math.abs(w) > GeoUtil.EPSILON) {
            w = 1.0 / w;
            var newX = (x * m[0] + y * m[1] + m[2]) * w;
            var newY = (x * m[3] + y * m[4] + m[5]) * w;
            return new vector_1.Vector(newX, newY);
        }
        return new vector_1.Vector(0, 0);
    }
    projectWorldToCalibrated(point_MRW) {
        var currentPoint_CI = Project.WorldToCalibrated(this.focalLength, this.resolution, point_MRW);
        if (this.calibration == null) {
            return currentPoint_CI;
        }
        // If the point is way outside of the resolution of the image then ignore the radial distortion.
        if (currentPoint_CI.x > this.resolution.width ||
            currentPoint_CI.y > this.resolution.height ||
            currentPoint_CI.y < 0 ||
            currentPoint_CI.x < 0) {
            return currentPoint_CI;
        }
        return CameraCalibration.Distort(this.calibration, this.resolution, this.rotation, currentPoint_CI);
    }
    projectCalibratedToWorld(point_CI) {
        var currentPoint_CI = point_CI;
        if (this.calibration != null) {
            // Only undisort points inside the size of the image.
            if (currentPoint_CI.x <= this.resolution.width &&
                currentPoint_CI.y <= this.resolution.height &&
                currentPoint_CI.y >= 0 &&
                currentPoint_CI.x >= 0) {
                currentPoint_CI = CameraCalibration.UnDistort(this.calibration, this.resolution, this.rotation, currentPoint_CI);
            }
        }
        return Project.CalibratedToWorld(this.focalLength, this.planeCenter_MRW, this.planeNormal_MRW, this.resolution, currentPoint_CI);
    }
    projectCalibratedToImage(point_CI) {
        return Project.CalibratedToImage(this.resolution, point_CI);
    }
    projectMeasureToCalibrated(point_MP) {
        return Project.MeasureToCalibrated(this.focalLength, this.planeOrigin_MRW, this.planeAxis_MP, this.resolution, point_MP);
    }
    projectCalibratedToMeasure(point_CI) {
        return this.perspectiveTransform(this.projectCalibratedToImage(point_CI));
    }
}
exports.Geometry3D = Geometry3D;
//# sourceMappingURL=geometry.3d.js.map